import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { DOCUMENT_ALLOWED_CURRENCIES } from "@/modules/quotation-invoice/domain/document-currencies";
import {
  type IssuerBankAccountsByCurrency,
  mergeIssuerBankAccounts,
  normalizeIssuerBankAccounts,
} from "@/modules/quotation-invoice/domain/issuer-bank-accounts";
import { AvatarPresignedUpload } from "@/shared/ui/AvatarPresignedUpload";
import {
  useQuotationInvoiceIssuerSettingsQuery,
  useUpdateQuotationInvoiceIssuerSettings,
} from "../api/queries";

export function QuotationInvoiceIssuerSettingsPage() {
  const nav = useNavigate({ from: "/app/documents/settings" });
  const canUpdate = useActionPermission(["quotation_invoice:update"]);
  const { data, isLoading } = useQuotationInvoiceIssuerSettingsQuery();
  const updateMutation = useUpdateQuotationInvoiceIssuerSettings();

  const [companyName, setCompanyName] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [companyContact, setCompanyContact] = useState("");
  const [signatureImageKey, setSignatureImageKey] = useState<string | null>(
    null,
  );
  const [companyStampKey, setCompanyStampKey] = useState<string | null>(null);
  const [companyLogoKey, setCompanyLogoKey] = useState<string | null>(null);
  const [bankAccounts, setBankAccounts] =
    useState<IssuerBankAccountsByCurrency>(() =>
      normalizeIssuerBankAccounts(undefined),
    );

  useEffect(() => {
    if (data) {
      setCompanyName(data.companyName);
      setCompanyAddress(data.companyAddress);
      setCompanyContact(data.companyContact);
      setSignatureImageKey(data.signatureImageKey);
      setCompanyStampKey(data.companyStampKey);
      setCompanyLogoKey(data.companyLogoKey);
      setBankAccounts(normalizeIssuerBankAccounts(data.bankAccounts));
    }
  }, [data]);

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="gap-2"
            onClick={() =>
              nav({
                to: "/app/documents",
                search: {
                  offset: 0,
                  limit: 20,
                  sort: undefined,
                  filters: undefined,
                },
              })
            }
          >
            <ArrowLeftIcon className="h-4 w-4" />
            ກັບໄປລາຍການເອກະສານ
          </Button>
        </div>

        <Card className="mx-auto max-w-2xl">
          <CardHeader>
            <CardTitle>ຕັ້ງຄ່າຜູ້ອອກເອກະສານ</CardTitle>
            <p className="text-muted-foreground text-sm">
              ຊື່ບໍລິສັດ, ທີ່ຢູ່, ຕິດຕໍ່, ໂລໂກ, ຮູບລາຍເຊັນ, ຕຣາປະທັບ, ບັນຊີ ຮ່ວມທັງ QR ຮັບເງິນຕາມສະກຸນ
              — ໃຊ້ຮ່ວມກັນທຸກໃບລາຄາ / ໃບບິນ / ໃບຮັບເງິນ ແລະ PDF
            </p>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
            ) : (
              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!canUpdate) return;
                  updateMutation.mutate({
                    companyName,
                    companyAddress,
                    companyContact,
                    signatureImageKey,
                    companyStampKey,
                    companyLogoKey,
                    bankAccounts: mergeIssuerBankAccounts(bankAccounts),
                  });
                }}
              >
                <div>
                  <Label htmlFor="issuer-name">ຊື່ບໍລິສັດ / ອົງກອນ</Label>
                  <Input
                    id="issuer-name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    disabled={!canUpdate}
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="issuer-address">ທີ່ຢູ່</Label>
                  <Input
                    id="issuer-address"
                    value={companyAddress}
                    onChange={(e) => setCompanyAddress(e.target.value)}
                    disabled={!canUpdate}
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="issuer-contact">ຕິດຕໍ່ (ອີເມວ, ໂທລະສັບ)</Label>
                  <Input
                    id="issuer-contact"
                    value={companyContact}
                    onChange={(e) => setCompanyContact(e.target.value)}
                    disabled={!canUpdate}
                    className="mt-1"
                    required
                  />
                </div>
                <AvatarPresignedUpload
                  label="ໂລໂກບໍລິສັດ"
                  hint="PNG ຫຼື JPG — ສະແດງຫົວເອກະສານການເງິນ ແລະ PDF"
                  value={companyLogoKey}
                  onChange={setCompanyLogoKey}
                  disabled={!canUpdate}
                  keyPrefix="uploads/quotation-invoice/logo"
                  previewClassName="relative h-20 w-40 shrink-0 overflow-hidden rounded-md border bg-muted"
                  imageFit="contain"
                />
                <div className="space-y-4 border-t pt-4">
                  <p className="font-medium text-sm">
                    ບັນຊີຮັບເງິນ ແລະ QR (ຕາມສະກຸນເງິນ)
                  </p>
                  <div className="grid gap-4 sm:grid-cols-1">
                    {DOCUMENT_ALLOWED_CURRENCIES.map((code) => (
                      <div
                        key={code}
                        className="rounded-lg border bg-muted/30 p-4"
                      >
                        <p className="mb-3 font-semibold text-sm">{code}</p>
                        <div className="space-y-3">
                          <div>
                            <Label htmlFor={`bank-num-${code}`}>ເລກບັນຊີ</Label>
                            <Input
                              id={`bank-num-${code}`}
                              value={bankAccounts[code].accountNumber}
                              onChange={(e) =>
                                setBankAccounts((prev) => ({
                                  ...prev,
                                  [code]: {
                                    ...prev[code],
                                    accountNumber: e.target.value,
                                  },
                                }))
                              }
                              disabled={!canUpdate}
                              className="mt-1"
                              autoComplete="off"
                            />
                          </div>
                          <div>
                            <Label htmlFor={`bank-name-${code}`}>ຊື່ບັນຊີ</Label>
                            <Input
                              id={`bank-name-${code}`}
                              value={bankAccounts[code].accountName}
                              onChange={(e) =>
                                setBankAccounts((prev) => ({
                                  ...prev,
                                  [code]: {
                                    ...prev[code],
                                    accountName: e.target.value,
                                  },
                                }))
                              }
                              disabled={!canUpdate}
                              className="mt-1"
                              autoComplete="off"
                            />
                          </div>
                          <AvatarPresignedUpload
                            label={`QR ຮັບເງິນ (${code})`}
                            hint="PNG ຫຼື JPG — ສະແດງໃນໃບບິນເມື່ອສະກຸນເງິນເອກະສານກົງກັນ"
                            value={bankAccounts[code].qrCodeImageKey}
                            onChange={(key) =>
                              setBankAccounts((prev) => ({
                                ...prev,
                                [code]: {
                                  ...prev[code],
                                  qrCodeImageKey: key,
                                },
                              }))
                            }
                            disabled={!canUpdate}
                            keyPrefix={`uploads/quotation-invoice/qr/${code}`}
                            previewClassName="relative mx-auto h-32 w-32 shrink-0 overflow-hidden rounded-md border bg-muted"
                            imageFit="contain"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <AvatarPresignedUpload
                  label="ລາຍເຊັນຜູ້ມີອຳນາດ (ຮູບ)"
                  hint="PNG ຫຼື JPG — ສະແດງໃນທ້າຍເອກະສານ ແລະ PDF"
                  value={signatureImageKey}
                  onChange={setSignatureImageKey}
                  disabled={!canUpdate}
                  keyPrefix="uploads/quotation-invoice/signature"
                  previewClassName="relative h-24 w-44 shrink-0 overflow-hidden rounded-md border bg-muted"
                  imageFit="contain"
                />
                <AvatarPresignedUpload
                  label="ຕຣາປະທັບບໍລິສັດ (ຮູບ)"
                  hint="ຮູບຕຣາປະທັບ — ສະແດງຄຽງລາຍເຊັນໃນເອກະສານ"
                  value={companyStampKey}
                  onChange={setCompanyStampKey}
                  disabled={!canUpdate}
                  keyPrefix="uploads/quotation-invoice/stamp"
                  previewClassName="relative mx-auto h-28 w-28 shrink-0 overflow-hidden rounded-md border bg-muted"
                  imageFit="contain"
                />
                {canUpdate ? (
                  <Button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="w-full sm:w-auto"
                  >
                    {updateMutation.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
                  </Button>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    ທ່ານບໍ່ມີສິດແກ້ໄຂ — ສາມາດເບິ່ງຢ່າງດຽວ
                  </p>
                )}
              </form>
            )}
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
